import { useCallback, useEffect, useMemo, useState } from 'react'

import { type MoneyAmount } from 'types'

import { CategoryFilter } from './CategoryFilter'
import { Footer } from './Footer'
import { Header } from './Header'
import { MarketplaceProducts } from './Products'
import { events, SessionContext } from '../shared'
import { type MarketplaceProductCategory, type SelectedCategory } from '../shared/types'

type MarketplaceProps = {
  balance?: MoneyAmount | null;
  member_id?: string | null;
  employer_id?: string | null;
  is_authenticated: boolean;
  public_view: boolean;
  public_referral_url: string;
  card_payments_enabled: boolean;
}

export default function Marketplace ({
  balance,
  member_id: memberId,
  employer_id: employerId,
  is_authenticated: isAuthenticated,
  public_view: publicView,
  public_referral_url: publicReferralUrl,
  card_payments_enabled: cardPaymentsEnabled,
}: MarketplaceProps) {
  const [activeCategories, setActiveCategories] = useState<SelectedCategory[]>([])
  const [startTime, setStartTime] = useState<number>(Date.now())
  const sessionContext = useMemo(() => ({
    id: memberId ?? null,
    employerId: employerId ?? null,
    balance: balance ?? undefined,
    isAuthenticated,
    publicView,
    publicReferralUrl,
  }), [balance, employerId, isAuthenticated, memberId, publicReferralUrl, publicView])

  const toggleCategory = useCallback((category: MarketplaceProductCategory) => {
    if (category === 'All') {
      setActiveCategories([])
      return
    }
    setActiveCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    )
  }, [])

  useEffect(() => {
    setStartTime(Date.now())
    events.emitProductsViewEvent(sessionContext)

    const handleBeforeUnload = () => {
      events.emitProductsExitEvent(sessionContext, Math.round((Date.now() - startTime) / 1000))
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [sessionContext])

  return (
    <SessionContext.Provider value={sessionContext}>
      <div className='@container'>
        <Header renderBalance={isAuthenticated && !!balance} cardPaymentsEnabled={cardPaymentsEnabled} />
        <CategoryFilter activeCategories={activeCategories} toggleCategory={toggleCategory} />
        <MarketplaceProducts activeCategories={activeCategories} />
        <Footer />
      </div>
    </SessionContext.Provider>
  )
}
